/**
 * « HOA me prévient quand » (Paramètres, H-Parametres).
 *
 * Les réglages vivent sur le téléphone. Ce que l'application peut tenir
 * sans service de notifications : prévenir tant qu'elle est ouverte (même
 * en arrière-plan) par une notification du navigateur, et un son court à
 * l'arrivée d'un message. Pour être réveillé application fermée, il y a
 * l'agenda (« Ajouter à mon agenda », rappel 30 min avant).
 */

export type Sorte = "session" | "part" | "arrivee" | "annulee" | "son";

export const LIBELLES: Record<Sorte, string> = {
  session: "Une session commence",
  part: "Ma tribu part",
  arrivee: "Quelqu'un arrive au rendez-vous",
  annulee: "Ma tribu est annulée",
  son: "Un message arrive (son)",
};

const CLE = "hoa.prevenir";
const DEFAUT: Record<Sorte, boolean> = { session: true, part: true, arrivee: true, annulee: true, son: false };

export function lirePreferences(): Record<Sorte, boolean> {
  try {
    return { ...DEFAUT, ...(JSON.parse(localStorage.getItem(CLE) ?? "{}") as Partial<Record<Sorte, boolean>>) };
  } catch {
    return { ...DEFAUT };
  }
}

export function ecrirePreference(s: Sorte, v: boolean): Record<Sorte, boolean> {
  const p = { ...lirePreferences(), [s]: v };
  try {
    localStorage.setItem(CLE, JSON.stringify(p));
  } catch {
    /* navigation privée */
  }
  // Demander l'autorisation au moment où l'on active, pas avant.
  if (v && s !== "son" && typeof Notification !== "undefined" && Notification.permission === "default") {
    void Notification.requestPermission().catch(() => undefined);
  }
  return p;
}

/** Une notification du navigateur, seulement si l'écran n'est pas déjà sous les yeux. */
export function notifier(s: Exclude<Sorte, "son">, texte: string): void {
  if (!lirePreferences()[s]) return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  if (document.visibilityState === "visible") return;
  try {
    new Notification("HOA", { body: texte, icon: "/icone-192.png", tag: `hoa-${s}` });
  } catch {
    /* certains navigateurs mobiles exigent un service worker : on se tait */
  }
}

/**
 * Demander l'autorisation des notifications, au moment d'un geste (entrer
 * dans une tribu) : sans geste, les navigateurs refusent de demander.
 */
export function demanderNotifications(): void {
  const p = lirePreferences();
  if (!p.part && !p.arrivee && !p.annulee) return;
  if (typeof Notification !== "undefined" && Notification.permission === "default") {
    void Notification.requestPermission().catch(() => undefined);
  }
}

/** Quelqu'un de la tribu arrive au rendez-vous : une vibration, et une notification si Hoa n'est pas à l'écran. */
export function prevenirArrivee(texte: string): void {
  if (!lirePreferences().arrivee) return;
  try {
    navigator.vibrate?.([90, 60, 90]);
  } catch {
    /* pas de vibreur (iPhone) : la bulle et la ligne du fil suffisent */
  }
  notifier("arrivee", texte);
}

let contexte: AudioContext | null = null;

/** Deux notes courtes à l'arrivée d'un message, si le réglage est allumé. */
export function bip(): void {
  if (!lirePreferences().son) return;
  try {
    const C = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!C) return;
    contexte = contexte ?? new C();
    const t = contexte.currentTime;
    for (const [i, f] of [660, 880].entries()) {
      const o = contexte.createOscillator();
      const g = contexte.createGain();
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t + i * 0.12);
      g.gain.exponentialRampToValueAtTime(0.18, t + i * 0.12 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.12 + 0.11);
      o.connect(g).connect(contexte.destination);
      o.start(t + i * 0.12);
      o.stop(t + i * 0.12 + 0.12);
    }
  } catch {
    /* pas de son : tant pis */
  }
}

/** La session pour laquelle on a demandé « Préviens-moi » (H-Prevenu). */
const CLE_SESSION = "hoa.prevenu";

export function lireSessionPrevenue(): { debut: number; fin: number } | null {
  try {
    const v = JSON.parse(localStorage.getItem(CLE_SESSION) ?? "null") as { debut: number; fin: number } | null;
    return v && v.fin > Date.now() ? v : null;
  } catch {
    return null;
  }
}

export function ecrireSessionPrevenue(s: { debut: number; fin: number } | null): void {
  try {
    if (s) localStorage.setItem(CLE_SESSION, JSON.stringify(s));
    else localStorage.removeItem(CLE_SESSION);
  } catch {
    /* navigation privée */
  }
  if (s && typeof Notification !== "undefined" && Notification.permission === "default") {
    void Notification.requestPermission().catch(() => undefined);
  }
}
